import React, { useState, useEffect } from 'react';
import { DialogField, DialogOption, VariableCondition, DialogVariable } from '@/lib/stores/dialogStore';
import { IconProvider } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';
import { useDisplayLanguageOptional, resolveOptionDisplayText } from '@/contexts/DisplayLanguageContext';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

interface DialogFieldPlayDisplayProps {
  field: DialogField;
  sectionIndex: number;
  fieldIndex: number;
  speakerMode?: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  gameId?: string;
  animationEnabled?: boolean;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onOptionClick?: (option: DialogOption, optionIndex: number) => void;
  showOptions: boolean;
  onTypingComplete?: () => void;
  evaluateCondition?: (condition: VariableCondition) => boolean;
  variables?: Record<string, boolean | number | string>;
  dialogVariables?: DialogVariable[];
}

export function DialogFieldPlayDisplay({
  field,
  sectionIndex,
  fieldIndex,
  speakerMode = 'none',
  speakerTypeId,
  speakerTemplateIds,
  gameId,
  animationEnabled = true,
  onUpdateField,
  onOptionClick,
  showOptions,
  onTypingComplete,
  evaluateCondition,
  variables,
  dialogVariables,
}: DialogFieldPlayDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.text);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  // Get speaker display name (only for 'dialog', 'question', and 'end-dialog' types)
  const getSpeakerName = (): string | null => {
    if (!field.speaker || speakerMode === 'none') return null;
    if (field.type !== 'dialog' && field.type !== 'question' && field.type !== 'end-dialog') return null;
    
    if (speakerMode === 'free-text') {
      return field.speaker;
    } else if (speakerMode === 'type' && gameId) {
      const types = getCachedTypes(gameId);
      const selectedType = speakerTypeId ? types.find(t => t.id === speakerTypeId) : null;
      const element = selectedType?.data?.find(e => e.id === field.speaker);
      return element?.name || null;
    } else if (speakerMode === 'object' && gameId) {
      const objects = getCachedObjects(gameId);
      const obj = objects.find(o => o.id === field.speaker);
      return obj?.name || null;
    }
    
    return null;
  };
  
  const speakerName = getSpeakerName();

  // Filter options by visibility condition
  const visibleOptions = field.options?.filter(option => {
    if (!option.visibilityCondition || !evaluateCondition) {
      return true; // Show option if no condition or no evaluator
    }
    return evaluateCondition(option.visibilityCondition);
  }) || [];

  // Strip HTML for display
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Typing animation effect - only trigger once when field first loads
  useEffect(() => {
    // If animation is disabled, show text immediately
    if (!animationEnabled) {
      setIsTyping(false);
      setDisplayedText(field.text);
      // Call typing complete immediately
      setTimeout(() => {
        onTypingComplete?.();
      }, 100); // Small delay to ensure smooth rendering
      return;
    }
    
    // Otherwise, do typing animation
    setIsTyping(true);
    setDisplayedText('');
    
    const plainText = stripHtml(field.text);
    let currentIndex = 0;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= plainText.length) {
        // Get the slice of plain text
        const textSlice = plainText.slice(0, currentIndex);
        
        // For now, show plain text during typing
        // After typing completes, show full HTML
        setDisplayedText(textSlice);
        currentIndex++;
      } else {
        setIsTyping(false);
        setDisplayedText(field.text); // Show full HTML version
        onTypingComplete?.(); // Notify parent that typing is done
        clearInterval(typingInterval);
      }
    }, 30); // 30ms per character for smooth typing

    return () => {
      clearInterval(typingInterval);
    };
    // Only trigger when field.id or animationEnabled changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.id, animationEnabled]);

  const handleSaveEdit = () => {
    onUpdateField(sectionIndex, field.id, { text: editValue });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(field.text);
    setIsEditing(false);
  };

  return (
    <div className="mb-6 group">
      {/* Field text display */}
      <div className="relative p-6">
        {isEditing ? (
          <div className="space-y-3">
            <TextAreaElement
              value={editValue}
              onChange={setEditValue}
              placeholder="Enter text..."
              rows={1}
              enableBubbleMenu={true}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSaveEdit}
                type="button"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Speaker name displayed before text with fade-in animation */}
            {speakerName && (
              <div className={`text-base font-semibold text-primary italic mb-1 ${animationEnabled ? 'animate-in fade-in duration-300' : 'animate-in fade-in duration-200'}`}>
                {speakerName}:
              </div>
            )}
            
            {isTyping ? (
              <div className={`text-lg leading-relaxed text-gray-800 text-left whitespace-pre-wrap ${field.type === 'context' ? 'italic' : ''}`}>
                {displayedText}
                <span className="animate-pulse">|</span>
              </div>
            ) : (
              <div
                className={`text-lg leading-relaxed text-gray-800 text-left ${field.type === 'context' ? 'italic' : ''}`}
                dangerouslySetInnerHTML={{ __html: displayedText }}
              />
            )}
            {/* Edit icon - appears on hover */}
            <button
              onClick={() => {
                setEditValue(field.text);
                setIsEditing(true);
              }}
              className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-gray-100 rounded-md"
              title="Edit text"
              type="button"
            >
              <IconProvider iconstring="fa-edit" size={14} className="text-gray-500" />
            </button>
          </>
        )}
      </div>

      {/* Condition field display - show what is being checked */}
      {field.type === 'condition' && field.condition && !isEditing && !isTyping && (() => {
        const checkVarName = field.condition!.checkVariable || field.condition!.variable;
        const isRangeCheck = field.conditionOptions?.some(opt => opt.operator === 'in_range');
        const checkValue = variables && checkVarName in variables ? variables[checkVarName] : undefined;
        
        // For range checks, resolve element IDs to names via TypeStore
        const varDecl = dialogVariables?.find(v => v.name === field.condition!.variable);
        const typeItem = (isRangeCheck && varDecl?.typeStoreRef?.typeId)
          ? getTypeItemById(varDecl.typeStoreRef.typeId)
          : null;
        
        const resolveOptionLabel = (opt: { operator?: string; condition_value: any }) => {
          if (opt.operator === 'in_range' && typeItem?.data) {
            const el = typeItem.data.find(e => e.id === String(opt.condition_value));
            return el?.name || String(opt.condition_value);
          }
          return String(opt.condition_value);
        };
        
        return (
          <div className={`mx-6 mt-2 ${animationEnabled ? 'animate-in fade-in slide-in-from-bottom-2 duration-500' : 'animate-in fade-in duration-300'}`}>
            <div className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                <IconProvider iconstring="fa-code-branch" size={14} />
                <span>Condition Check</span>
              </div>
              <div className="flex items-center gap-2 text-amber-900">
                {isRangeCheck ? (
                  <>
                    <span className="text-amber-600">Variable:</span>
                    <span className="font-mono font-semibold">{field.condition!.variable}</span>
                    <span className="text-amber-400">|</span>
                    <span className="text-amber-600">Checking:</span>
                    <span className="font-mono font-semibold">{checkVarName}</span>
                    <span className="text-amber-600">=</span>
                    <span className="font-mono font-bold text-amber-800">
                      {checkValue !== undefined ? String(checkValue) : <span className="italic text-amber-500">not set</span>}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-mono font-semibold">{checkVarName}</span>
                    <span className="text-amber-600">=</span>
                    <span className="font-mono font-bold text-amber-800">
                      {checkValue !== undefined ? String(checkValue) : <span className="italic text-amber-500">not set</span>}
                    </span>
                  </>
                )}
              </div>
              {field.conditionOptions && field.conditionOptions.length > 0 && (
                <div className="mt-2 space-y-1">
                  {field.conditionOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                      <span className="text-amber-400">&#8627;</span>
                      <span className="font-mono">
                        {opt.operator === 'in_range' ? 'in' : (opt.operator || '==')} {resolveOptionLabel(opt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-500">
                <div className="animate-pulse w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span>Evaluating...</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Setter field display - show what is being set */}
      {field.type === 'setter' && field.setter && !isEditing && !isTyping && (
        <div className={`mx-6 mt-2 ${animationEnabled ? 'animate-in fade-in slide-in-from-bottom-2 duration-500' : 'animate-in fade-in duration-300'}`}>
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
              <IconProvider iconstring="fa-pen" size={14} />
              <span>Variable Set</span>
            </div>
            <div className="flex items-center gap-2 text-emerald-900">
              <span className="font-mono font-semibold">{field.setter.variable}</span>
              <span className="text-emerald-600">
                {field.setter.operator === 'set' ? '←' : field.setter.operator === 'increment' ? '+=' : '-='}
              </span>
              <span className="font-mono font-bold text-emerald-800">{String(field.setter.value)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Options for question type - centered, show only after typing completes */}
      {field.type === 'question' && visibleOptions.length > 0 && showOptions && !isEditing && !isTyping && (
        <div className="mt-6 space-y-2 text-center">
          {visibleOptions.map((option, optionIndex) => (
            <div 
              key={optionIndex}
              className={animationEnabled ? 'animate-in fade-in slide-in-from-bottom-2 duration-500' : 'animate-in fade-in duration-300'}
              style={animationEnabled ? { animationDelay: `${optionIndex * 100}ms` } : undefined}
            >
              <OptionDisplay
                option={option}
                optionIndex={optionIndex}
                sectionIndex={sectionIndex}
                fieldId={field.id}
                onOptionClick={onOptionClick}
              />
            </div>
          ))}
        </div>
      )}

      {/* Visual indicator for end-text and end-dialog with fade in */}
      {(field.type === 'end-text' || field.type === 'end-dialog') && !isEditing && (
        <div className={`mt-4 text-center ${animationEnabled ? 'animate-in fade-in duration-500' : 'animate-in fade-in duration-300'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
            <span>End of dialog</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface OptionDisplayProps {
  option: DialogOption;
  optionIndex: number;
  sectionIndex: number;
  fieldId: string;
  onOptionClick?: (option: DialogOption, optionIndex: number) => void;
}

function OptionDisplay({
  option,
  optionIndex,
  onOptionClick,
}: OptionDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { selectedGame } = useSimplifiedUserData();
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const effectiveGameId = selectedGame?.id;

  const optionDisplayText = resolveOptionDisplayText(option, displayLanguage, effectiveGameId);

  return (
    <div className="group relative">
      {isEditing ? (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">
            Editing options in play mode is limited. Switch to Edit Mode for full control.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(false)}
            type="button"
          >
            Close
          </Button>
        </div>
      ) : (
        <button
          onClick={() => onOptionClick?.(option, optionIndex)}
          className="w-full text-center py-2 hover:text-gray-900 hover:underline transition-all duration-200"
          type="button"
        >
          <div
            className="text-base text-gray-500"
            dangerouslySetInnerHTML={{ __html: optionDisplayText }}
          />
          {/* Small edit icon on hover */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
            title="Edit option"
            type="button"
          >
            <IconProvider iconstring="fa-edit" size={12} className="text-gray-500" />
          </button>
        </button>
      )}
    </div>
  );
}

